import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import {
  Target,
  Shield,
  Clock,
  MapPin,
  Trophy,
  DollarSign,
  Ruler,
  ArrowRight,
} from "lucide-react";

function parseDT(s) {
  if (!s) return null;
  const d = new Date(String(s).replace(" ", "T"));
  return isNaN(d.getTime()) ? null : d;
}

function formatDateTime(dt) {
  const d = parseDT(dt);
  if (!d) return "TBD";
  return d.toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Renders the match name as a clickable link that opens a popup dialog
 * with full match information. Pass the `match` object directly; the parent
 * page already has it loaded so we avoid extra network calls.
 *
 * Optional `prefix` prepends the match number when displayed.
 */
export default function MatchInfoDialog({ match, eventName, className = "", prefix = false, children }) {
  const [open, setOpen] = useState(false);
  const { isAdmin } = useAuth();

  if (!match) return null;

  const displayName = children || (
    <>
      {prefix && match.match_number ? (
        <span className="font-mono text-xs bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded mr-2">
          #{match.match_number}
        </span>
      ) : null}
      {match.name}
    </>
  );

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }}
        className={`text-left hover:underline text-[#1B3B6F] font-medium ${className}`}
        data-testid={`match-info-link-${match.id}`}
      >
        {displayName}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2">
              <div className={`p-1.5 rounded-md ${match.is_protection ? "bg-purple-100" : "bg-green-100"}`}>
                {match.is_protection ? (
                  <Shield size={18} className="text-purple-600" />
                ) : (
                  <Target size={18} className="text-green-600" />
                )}
              </div>
              {match.name}
              {match.match_number && (
                <Badge variant="outline" className="ml-1">#{match.match_number}</Badge>
              )}
            </DialogTitle>
            {eventName && <DialogDescription>{eventName}</DialogDescription>}
          </DialogHeader>

          <div className="space-y-3 mt-2 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-start gap-2">
                <Clock size={16} className="text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-slate-500">Start</p>
                  <p className="font-medium text-slate-800">{formatDateTime(match.start_time)}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Clock size={16} className="text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-slate-500">End</p>
                  <p className="font-medium text-slate-800">{formatDateTime(match.end_time)}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Target size={16} className="text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-slate-500">Shots / round</p>
                  <p className="font-medium text-slate-800">{match.shots_per_round || "—"}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <MapPin size={16} className="text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-slate-500">Trap</p>
                  <p className="font-medium text-slate-800">{match.trap_number || match.trap_range || "—"}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Ruler size={16} className="text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-slate-500">Yardage</p>
                  <p className="font-medium text-slate-800">{match.yardage || "—"}</p>
                </div>
              </div>
              {isAdmin() && (
                <div className="flex items-start gap-2">
                  <DollarSign size={16} className="text-slate-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-slate-500">Entry Fee</p>
                    <p className="font-medium text-slate-800">${parseFloat(match.entry_fee || 0).toFixed(2)}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2 pt-1">
              {match.is_protection && (
                <Badge variant="outline" className="text-purple-600">
                  <Shield size={12} className="mr-1" /> Protection
                </Badge>
              )}
              {match.is_championship && (
                <Badge className="bg-yellow-100 text-yellow-800">Aggregate</Badge>
              )}
              {match.status && (
                <Badge variant="outline" className="capitalize">{match.status}</Badge>
              )}
            </div>

            <div className="pt-3 border-t flex flex-wrap gap-2 justify-end">
              <Link to={`/leaderboard/${match.id}`} onClick={() => setOpen(false)}>
                <Button variant="outline" size="sm">
                  <Trophy size={14} className="mr-1" />
                  Leaderboard
                </Button>
              </Link>
              {match.event_id && (
                <Link to={`/event-info/${match.event_id}`} onClick={() => setOpen(false)}>
                  <Button size="sm" className="bg-[#1B3B6F] hover:bg-[#1B3B6F]/90">
                    View Event
                    <ArrowRight size={14} className="ml-1" />
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
